import { useState, type ReactNode } from "react";
import { EmojiGrid } from "@/components/sms-editor/SmsEmojiPicker";
import { Popover, PopoverContent, PopoverTrigger } from "@/v5/components/ui/popover";

interface EmojiPickerButtonProps {
  /** Receives the chosen emoji; the host inserts it at its own cursor. */
  onSelect: (emoji: string) => void;
  children: ReactNode;
  className?: string;
  title?: string;
  disabled?: boolean;
  side?: "top" | "bottom";
  align?: "start" | "center" | "end";
  /** Pass false inside a Dialog — see PopoverContent's `portal`. */
  portal?: boolean;
}

/**
 * A trigger button that opens the SMS editor's emoji grid in a Popover.
 *
 * The SMS editors render SmsEmojiPicker as an absolutely positioned panel,
 * which an overflow-hidden thread or a scrolling dialog body would clip. The
 * Popover escapes both and flips to stay on screen: by portalling, or inside a
 * Dialog (`portal={false}`) through fixed positioning against the Dialog's
 * transformed box, which the scrolling body inside it cannot clip. The trigger
 * is a native <button> because the v5 Button does not forward refs, and Radix
 * anchors the Popover to the trigger's ref.
 */
export function EmojiPickerButton({
  onSelect,
  children,
  className,
  title = "Insert emoji",
  disabled,
  side = "top",
  align = "start",
  portal = true,
}: EmojiPickerButtonProps) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={className} title={title} aria-label={title} disabled={disabled}>
          {children}
        </button>
      </PopoverTrigger>
      <PopoverContent
        portal={portal}
        side={side}
        align={align}
        className="flex h-[380px] w-[calc(100vw-1rem)] sm:w-[420px] flex-col p-0"
        // The host moves focus back into the field the emoji went into; letting
        // Radix return it to this button would pull the caret out again.
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <EmojiGrid
          onSelect={(emoji) => {
            onSelect(emoji);
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
